import React, { useState, useEffect, useCallback } from 'react';
import { osintAPI } from '../../services/api';

const OSINTTab = ({ person, addNotification }) => {
  const [scanning, setScanning] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [results, setResults] = useState(null);
  const [lastScanInfo, setLastScanInfo] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedFindings, setSelectedFindings] = useState([]);

  const loadResults = useCallback(async () => {
    try {
      const data = await osintAPI.getResults(person.id);
      if (data.has_results) {
        setResults(data.findings);
        setLastScanInfo(data.last_scan);
      } else if (data.has_scan_in_progress) {
        setScanning(true);
        setCurrentJob({ id: data.job_id, progress: data.progress, message: data.progress_message });
        pollJobStatus(data.job_id);
      } else {
        setResults(null);
        setLastScanInfo(null);
      }
    } catch (err) {
      console.error('Failed to load OSINT results:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const startScan = async () => {
    try {
      const res = await osintAPI.startScan(person.id);
      setCurrentJob({ id: res.job_id, progress: 0, message: 'Starting scan...' });
      setScanning(true);
      setResults(null);
      setSelectedFindings([]);

      if (addNotification) {
        addNotification({
          type: 'info',
          title: 'OSINT Scan Started',
          message: `Searching for "${person.name}" in background...`,
          persistent: false,
        });
      }

      pollJobStatus(res.job_id);
    } catch (err) {
      console.error('Failed to start scan:', err);
      setScanning(false);
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Scan Failed',
          message: err.response?.data?.detail || 'Failed to start OSINT scan',
        });
      }
    }
  };

  const pollJobStatus = async (jobId) => {
    const poll = async () => {
      try {
        const status = await osintAPI.getStatus(jobId);
        setCurrentJob({
          id: jobId,
          progress: status.progress,
          message: status.progress_message,
          status: status.status,
        });

        if (status.status === 'completed') {
          setScanning(false);
          loadResults();

          if (addNotification) {
            addNotification({
              type: 'osint_complete',
              title: 'OSINT Scan Complete',
              message: `Found ${status.total_findings} results for "${person.name}"`,
              action: {
                label: 'View Results',
              },
              persistent: true,
            });
          }
          return;
        }

        if (status.status === 'failed') {
          setScanning(false);
          if (addNotification) {
            addNotification({
              type: 'error',
              title: 'Scan Failed',
              message: status.error || 'An error occurred during the scan',
            });
          }
          return;
        }

        // Continue polling
        setTimeout(poll, 3000);
      } catch (err) {
        console.error('Polling error:', err);
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleMerge = async () => {
    if (selectedFindings.length === 0) return;

    try {
      const res = await osintAPI.mergeResults(person.id, selectedFindings, 'merge');
      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Data Merged',
          message: `Merged ${res.merged_count} findings into profile`,
        });
      }
      setSelectedFindings([]);
    } catch (err) {
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Merge Failed',
          message: 'Failed to merge findings',
        });
      }
    }
  };

  const toggleSelection = (findingId) => {
    setSelectedFindings(prev =>
      prev.includes(findingId)
        ? prev.filter(id => id !== findingId)
        : [...prev, findingId]
    );
  };

  const getLikelihoodColor = (likelihood) => {
    if (likelihood >= 80) return 'text-tech-success';
    if (likelihood >= 50) return 'text-yellow-500';
    return 'text-tech-danger';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      social_profiles: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
      ),
      documents: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
        </svg>
      ),
      contact_info: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
        </svg>
      ),
      images: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
        </svg>
      ),
      location_evidence: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
        </svg>
      ),
      breach: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
        </svg>
      ),
    };
    return icons[category] || icons.social_profiles;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      social_profiles: 'Social Profiles',
      documents: 'Documents',
      contact_info: 'Contact Info',
      images: 'Images',
      location_evidence: 'Location',
      employment: 'Employment',
      personal_details: 'Personal Details',
      news_mentions: 'News Mentions',
      sensitive_exposure: 'Sensitive Data',
      breach: 'Breach Data',
      default: 'Other'
    };
    return labels[category] || labels.default;
  };

  const filteredResults = results
    ? results.filter(r => {
        if (filter === 'all') return true;
        if (filter === 'high') return r.overall_likelihood >= 80;
        if (filter === 'medium') return r.overall_likelihood >= 50 && r.overall_likelihood < 80;
        if (filter === 'low') return r.overall_likelihood < 50;
        return true;
      })
    : [];

  const groupedResults = filteredResults.reduce((acc, result) => {
    const cat = result.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(result);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="osint-panel max-w-6xl mx-auto space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-tech-text">OSINT Intelligence</h2>
          {lastScanInfo && (
            <p className="text-sm text-tech-textMuted">
              Last scan: {formatDate(lastScanInfo.completed_at)} - {lastScanInfo.total_findings} findings
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={startScan}
            disabled={scanning}
            className="flex items-center gap-2 px-4 py-2 bg-tech-accent text-white rounded hover:bg-tech-accent/90 disabled:opacity-50 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            {scanning ? 'Scanning...' : 'Run OSINT Scan'}
          </button>
        </div>
      </div>

      {/* Scanning Progress */}
      {scanning && currentJob && (
        <div className="tech-card p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-tech-text font-medium">{currentJob.message || 'Scanning...'}</span>
            <span className="text-tech-accent font-mono">{currentJob.progress}%</span>
          </div>
          <div className="w-full bg-tech-surface rounded-full h-3">
            <div
              className="bg-tech-accent h-3 rounded-full transition-all duration-500"
              style={{ width: `${currentJob.progress}%` }}
            />
          </div>
          <p className="text-xs text-tech-textMuted mt-3">
            You can navigate away - we will notify you when the scan is complete
          </p>
        </div>
      )}

      {/* Filter Bar */}
      {results && results.length > 0 && !scanning && (
        <div className="flex items-center gap-4">
          <span className="text-sm text-tech-textMuted">Filter by likelihood:</span>
          {['all', 'high', 'medium', 'low'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded ${
                filter === f
                  ? 'bg-tech-accent text-white'
                  : 'bg-tech-surface text-tech-textMuted hover:bg-tech-surface/80'
              }`}
            >
              {f === 'all' ? 'All' : f === 'high' ? 'High (80%+)' : f === 'medium' ? 'Medium (50-79%)' : 'Low (<50%)'}
            </button>
          ))}
          {selectedFindings.length > 0 && (
            <button
              onClick={handleMerge}
              className="ml-auto px-4 py-2 bg-tech-success text-white rounded hover:bg-tech-success/90 transition"
            >
              Merge Selected ({selectedFindings.length})
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {!scanning && results && results.length > 0 && (
        <div className="space-y-6">
          {Object.entries(groupedResults).map(([category, items]) => (
            <div key={category} className="tech-card overflow-hidden">
              <div className="bg-tech-surface px-4 py-3 flex items-center gap-3 border-b border-tech-border">
                <span className="text-tech-accent">{getCategoryIcon(category)}</span>
                <h3 className="font-semibold text-tech-text">{getCategoryLabel(category)}</h3>
                <span className="ml-auto text-sm text-tech-textMuted">{items.length} results</span>
              </div>
              <div className="divide-y divide-tech-border">
                {items.map((finding, idx) => (
                  <div
                    key={finding.id || idx}
                    className={`p-4 hover:bg-tech-surface/50 transition ${
                      selectedFindings.includes(finding.id) ? 'bg-tech-accent/10 border-l-4 border-tech-accent' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Selection checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedFindings.includes(finding.id)}
                        onChange={() => toggleSelection(finding.id)}
                        className="mt-1 w-4 h-4 rounded border-tech-border text-tech-accent focus:ring-tech-accent"
                      />

                      {/* Likelihood indicator */}
                      <div className="flex-shrink-0 text-center">
                        <div className={`text-2xl font-bold ${getLikelihoodColor(finding.overall_likelihood)}`}>
                          {Math.round(finding.overall_likelihood)}%
                        </div>
                        <div className="text-xs text-tech-textMuted">Likelihood</div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            finding.risk_level === 'high'
                              ? 'bg-tech-danger/20 text-tech-danger'
                              : finding.risk_level === 'medium'
                              ? 'bg-yellow-500/20 text-yellow-500'
                              : 'bg-tech-surface text-tech-textMuted'
                          }`}>
                            {finding.source}
                          </span>
                          {finding.matched_fields?.length > 0 && (
                            <span className="text-xs text-tech-textMuted">
                              Matched: {finding.matched_fields.join(', ')}
                            </span>
                          )}
                        </div>

                        <h4 className="text-tech-text font-medium truncate">{finding.title}</h4>
                        <p className="text-sm text-tech-textMuted line-clamp-2">{finding.snippet}</p>

                        {/* Extracted data */}
                        {finding.extracted_data && Object.keys(finding.extracted_data).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {finding.extracted_data.emails?.map((email, i) => (
                              <span key={`e${i}`} className="px-2 py-1 bg-tech-surface text-xs rounded text-tech-accent">
                                {email}
                              </span>
                            ))}
                            {finding.extracted_data.phones?.map((phone, i) => (
                              <span key={`p${i}`} className="px-2 py-1 bg-tech-surface text-xs rounded text-tech-accent">
                                {phone}
                              </span>
                            ))}
                            {finding.extracted_data.social_handles?.map((handle, i) => (
                              <span key={`s${i}`} className="px-2 py-1 bg-tech-surface text-xs rounded text-tech-accent">
                                {handle}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="mt-3 flex gap-2">
                          {finding.url && (
                            <a
                              href={finding.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-tech-accent hover:underline flex items-center gap-1"
                            >
                              View Source
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!scanning && (!results || results.length === 0) && (
        <div className="tech-card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-tech-surface rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-tech-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-tech-text mb-2">No OSINT Data Yet</h3>
          <p className="text-tech-textMuted mb-6">
            Run a scan to search for public information about this person
          </p>
          <button
            onClick={startScan}
            className="px-6 py-3 bg-tech-accent text-white rounded-lg hover:bg-tech-accent/90 transition"
          >
            Start OSINT Scan
          </button>
        </div>
      )}
    </div>
  );
};

export default OSINTTab;
